<template>
  <div class="mw-live-player">
    <div
      class="ratio ratio-16x9 bg-dark rounded overflow-hidden position-relative"
    >
      <video
        ref="videoEl"
        class="w-100 h-100"
        style="object-fit: contain; background: #000"
        muted
        playsinline
        controls
      ></video>

      <!-- Loading: staged "waking the camera" animation. A headless camera
           needs a few seconds to spin its encoder up; this narrates the wait so
           it reads as intentional (and explains the power-saving reason). -->
      <div v-if="state === 'loading'" class="mw-loader" aria-live="polite">
        <div class="mw-loader__pulse" aria-hidden="true">
          <span class="mw-loader__ring"></span>
          <span class="mw-loader__ring"></span>
          <span class="mw-loader__ring"></span>
          <span class="mw-loader__core">
            <i class="bi bi-camera-reels-fill"></i>
          </span>
        </div>
        <div :key="loadStage" class="mw-loader__label">{{ loadLabel }}</div>
        <div
          class="mw-loader__track"
          role="progressbar"
          :aria-valuenow="Math.round(loadProgress)"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div class="mw-loader__fill" :style="{ width: loadProgress + '%' }"></div>
        </div>
        <div class="mw-loader__hint">
          <i class="bi bi-lightning-charge-fill"></i>
          Mode hemat daya — video muncul dalam beberapa detik
        </div>
      </div>

      <!-- Terminal states: offline / error / off / idle -->
      <div
        v-else-if="overlayText"
        class="position-absolute top-50 start-50 translate-middle text-center px-5"
        style="pointer-events: none"
      >
        <div class="mb-2">
          <i
            class="bi text-gray-500"
            :class="
              state === 'off'
                ? 'bi-power'
                : state === 'offline'
                ? 'bi-camera-video-off'
                : 'bi-exclamation-triangle'
            "
            style="font-size: 2rem"
          ></i>
        </div>
        <div class="text-gray-300 fs-7">{{ overlayText }}</div>
      </div>

      <!-- Streaming schedule overlay (bottom-left, clear of the right-aligned
           CV counter HUD burned into the video). Info-only, never blocks clicks. -->
      <div
        v-if="scheduled"
        class="position-absolute rounded px-2 py-1"
        style="
          left: 8px;
          bottom: 8px;
          top: auto;
          right: auto;
          width: auto;
          height: auto;
          background: rgba(0, 0, 0, 0.58);
          max-width: 70%;
          pointer-events: none;
          z-index: 5;
        "
      >
        <div
          class="d-flex align-items-center text-white fw-bold"
          style="font-size: 10.5px"
        >
          <i
            class="bi me-1"
            :class="
              scheduleActive
                ? 'bi-record-circle-fill text-success'
                : 'bi-pause-circle-fill text-warning'
            "
          ></i>
          {{ scheduleActive ? "Streaming aktif" : "Jeda otomatis (hemat kuota)" }}
        </div>
        <div class="text-white-50" style="font-size: 9.5px">
          Jadwal: {{ scheduleLabel }} WIB
        </div>
      </div>
    </div>

    <div
      v-if="showFooter"
      class="d-flex align-items-center justify-content-between mt-2"
    >
      <span class="badge" :class="liveBadgeClass">
        <span
          v-if="state === 'playing'"
          class="bullet bullet-dot bg-white me-2 mb-1"
        ></span>
        {{ liveBadgeText }}
      </span>
      <button
        type="button"
        class="btn btn-sm btn-light-primary"
        @click="reload"
        :disabled="state === 'loading'"
      >
        <i class="bi bi-arrow-clockwise me-1"></i> Muat Ulang
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
} from "vue";
import { deviceApiService } from "@/services/DeviceApiService";

// HLS base where the CV workers push the overlaid live streams.
const HLS_BASE =
  process.env.VUE_APP_HLS_BASE_URL || "https://vps.techiro.co.id:7443/hls";

// hls.js is loaded on demand from CDN (same version proven on mw-stream).
const HLS_JS_CDN =
  "https://cdn.jsdelivr.net/npm/hls.js@1.5.13/dist/hls.min.js";

let hlsLoaderPromise: Promise<any> | null = null;
function loadHlsLib(): Promise<any> {
  const w = window as any;
  if (w.Hls) return Promise.resolve(w.Hls);
  if (hlsLoaderPromise) return hlsLoaderPromise;
  hlsLoaderPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = HLS_JS_CDN;
    s.async = true;
    s.onload = () => resolve((window as any).Hls);
    s.onerror = () => {
      hlsLoaderPromise = null;
      reject(new Error("Gagal memuat pemutar (hls.js)"));
    };
    document.head.appendChild(s);
  });
  return hlsLoaderPromise;
}

type PlayerState =
  | "idle"
  | "loading"
  | "playing"
  | "offline"
  | "error"
  | "off"; // streaming intentionally disconnected (saves Tuya quota)

export default defineComponent({
  name: "live-stream-player",
  props: {
    streamKey: {
      type: String,
      default: "",
    },
    // When false, the player is torn down (used to pause when a modal closes).
    active: {
      type: Boolean,
      default: true,
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
    // "cv" = Contabo overlay HLS (default). "direct" = raw feed straight from
    // Tuya (used when the AI counter overlay is turned OFF for this camera).
    mode: {
      type: String,
      default: "cv",
    },
    // Required for "direct" mode: which device to allocate a Tuya URL for.
    deviceId: {
      type: Number,
      default: 0,
    },
    // Optional streaming-schedule badge shown bottom-left over the video.
    scheduled: {
      type: Boolean,
      default: false,
    },
    scheduleActive: {
      type: Boolean,
      default: true,
    },
    scheduleLabel: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const videoEl = ref<HTMLVideoElement | null>(null);
    const state = ref<PlayerState>("idle");
    let hls: any = null;

    // The CV-overlay source is a stable URL; the direct source is a short-lived
    // signed Tuya URL re-allocated on demand (resolved in start()).
    const resolvedSrc = ref("");
    let directRefreshTimer: any = null;

    const cvSrc = computed(() =>
      props.streamKey ? `${HLS_BASE}/${props.streamKey}.m3u8` : ""
    );

    /**
     * Resolve the playlist URL for the current mode. For "direct" we ask the
     * backend for a fresh Tuya HLS URL (signed + short-lived). Returns "" when
     * unavailable so the caller can show a graceful offline/error state.
     */
    const resolveSrc = async (): Promise<string> => {
      if (props.mode === "direct") {
        if (!props.deviceId) return "";
        const res = await deviceApiService.getLiveStreamUrl(props.deviceId, "HLS");
        return res?.url || "";
      }
      return cvSrc.value;
    };

    const overlayText = computed(() => {
      switch (state.value) {
        case "loading":
          return props.mode === "direct"
            ? "Menghubungkan langsung ke kamera (tanpa overlay)…"
            : "Menghubungkan ke kamera…";
        case "offline":
          return props.mode === "direct"
            ? "Kamera sedang offline / tidak dapat diakses dari Tuya."
            : "Kamera sedang offline / tidak mengirim siaran.";
        case "error":
          return "Gagal memuat siaran. Coba muat ulang.";
        case "off":
          return "Siaran dimatikan untuk menghemat kuota. Aktifkan kembali untuk menonton.";
        case "idle":
          return props.mode === "direct"
            ? "Kamera ini tidak mendukung siaran langsung."
            : "Stream key tidak tersedia.";
        default:
          return "";
      }
    });

    const liveBadgeText = computed(() =>
      state.value === "playing" ? "LIVE" : "Tidak aktif"
    );
    const liveBadgeClass = computed(() =>
      state.value === "playing" ? "badge-danger" : "badge-light"
    );

    // Auto-retry state: camera streams reconnect every few minutes (Tuya RTSP
    // TTL), briefly 404-ing the playlist during an ffmpeg restart. We retry
    // through those gaps instead of failing permanently.
    let retryTimer: any = null;
    let retryCount = 0;

    // Wake-on-view: a headless camera's CV worker runs the expensive ffmpeg
    // encode only while a viewer is present (per-camera `headless_count`). Ping
    // the backend on open + every 25s (< the 45s server TTL) so the overlay
    // stream spins up and stays up while watching. Only "cv" mode uses the
    // worker; "direct"/"off" must NOT ping (would wake the encoder for nobody).
    let wakeTimer: any = null;
    const stopWake = () => {
      if (wakeTimer) {
        clearInterval(wakeTimer);
        wakeTimer = null;
      }
    };
    const startWake = () => {
      stopWake();
      if (props.mode !== "cv" || !props.deviceId) return;
      const ping = () => {
        deviceApiService.pingLiveView(props.deviceId).catch(() => {});
      };
      ping();
      wakeTimer = setInterval(ping, 25000);
    };

    // ---- Staged "waking the camera" loader narrative --------------------
    // A headless camera needs ~6s to spin its encoder up before the overlay
    // HLS exists; a bare spinner reads as "stuck/broken". Instead we advance a
    // labelled, eased progress narrative so the wait feels intentional and
    // premium — and quietly explains the power-saving reason for it.
    const loadStage = ref(0);
    const loadProgress = ref(0);
    const LOAD_LABELS = [
      "Menghubungkan ke kamera…",
      "Menyalakan siaran hemat daya…",
      "Memuat video langsung…",
      "Sebentar lagi, kamera baru dinyalakan…",
    ];
    const loadLabel = computed(
      () => LOAD_LABELS[loadStage.value] || LOAD_LABELS[0]
    );
    let loadTimers: any[] = [];
    const clearLoad = () => {
      loadTimers.forEach((t) => clearTimeout(t));
      loadTimers = [];
    };
    const beginLoad = () => {
      clearLoad();
      loadStage.value = 0;
      loadProgress.value = 12;
      // Eased milestones tuned to the ~6s headless wake + HLS first-buffer.
      loadTimers.push(setTimeout(() => (loadProgress.value = 30), 120));
      loadTimers.push(
        setTimeout(() => {
          loadStage.value = 1;
          loadProgress.value = 60;
        }, 2200)
      );
      loadTimers.push(
        setTimeout(() => {
          loadStage.value = 2;
          loadProgress.value = 84;
        }, 5000)
      );
      loadTimers.push(
        setTimeout(() => {
          loadStage.value = 3;
          loadProgress.value = 93;
        }, 9000)
      );
    };

    const destroy = () => {
      stopWake();
      clearLoad();
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (directRefreshTimer) {
        clearTimeout(directRefreshTimer);
        directRefreshTimer = null;
      }
      if (hls) {
        try {
          hls.destroy();
        } catch (_) {
          /* ignore */
        }
        hls = null;
      }
      const v = videoEl.value;
      if (v) {
        try {
          v.pause();
          v.removeAttribute("src");
          v.load();
        } catch (_) {
          /* ignore */
        }
      }
    };

    // Rebuild the player after a short delay; keep trying so it self-heals when
    // the stream returns. Only surface "offline" after sustained failure.
    const scheduleReload = () => {
      if (retryTimer || !props.active) return;
      retryCount += 1;
      state.value = retryCount >= 5 ? "offline" : "loading";
      retryTimer = setTimeout(() => {
        retryTimer = null;
        start();
      }, 2500);
    };

    const start = async () => {
      destroy();
      if (!props.active) return;

      // Streaming is intentionally disconnected — never fetch/allocate so we
      // consume zero Tuya quota. Just show the "off" state.
      if (props.mode === "off") {
        state.value = "off";
        return;
      }

      // Spin up a headless camera's encoder ASAP (no-op for direct/off modes).
      startWake();

      const v = videoEl.value;
      if (!v) return;

      state.value = "loading";
      beginLoad();

      // Resolve the source (direct mode allocates a fresh Tuya URL each time,
      // which transparently handles signed-URL expiry on retry).
      let resolved = "";
      try {
        resolved = await resolveSrc();
      } catch (_) {
        scheduleReload();
        return;
      }
      // The mode/active may have changed while awaiting; bail if so.
      if (!props.active || props.mode === "off") return;
      if (!resolved) {
        // No URL: idle for CV (no key) / offline for direct (allocate failed).
        state.value = props.mode === "direct" ? "offline" : "idle";
        return;
      }
      resolvedSrc.value = resolved;

      // Mark playing once frames actually arrive; reset retry counter.
      v.onplaying = () => {
        retryCount = 0;
        loadProgress.value = 100;
        clearLoad();
        state.value = "playing";
      };

      // In direct mode the Tuya signed URL expires after ~6 min of active
      // playback (measured), so re-allocate every 4 min — well under that, with
      // the error handler as a backstop. Fewer re-allocations = less Tuya quota.
      if (props.mode === "direct") {
        directRefreshTimer = setTimeout(() => {
          directRefreshTimer = null;
          if (props.active && state.value === "playing") start();
        }, 240000);
      }

      try {
        const Hls = await loadHlsLib();
        if (Hls && Hls.isSupported()) {
          hls = new Hls({
            liveSyncDurationCount: 3,
            lowLatencyMode: false,
            // Be patient through reconnect gaps rather than erroring out.
            manifestLoadingMaxRetry: 8,
            manifestLoadingRetryDelay: 1000,
            manifestLoadingMaxRetryTimeout: 16000,
            levelLoadingMaxRetry: 8,
            levelLoadingRetryDelay: 1000,
            fragLoadingMaxRetry: 8,
            fragLoadingRetryDelay: 1000,
          });
          hls.loadSource(resolved);
          hls.attachMedia(v);
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            v.play().catch(() => {});
          });
          hls.on(Hls.Events.ERROR, (_evt: any, data: any) => {
            if (!data || !data.fatal) return;
            if (data.type === "mediaError") {
              try {
                hls.recoverMediaError();
                return;
              } catch (_) {
                scheduleReload();
              }
            } else {
              // Network errors (incl. a missing playlist during a reconnect
              // gap, or an expired direct URL): keep retrying — start() will
              // re-allocate a fresh URL in direct mode.
              scheduleReload();
            }
          });
        } else if (v.canPlayType("application/vnd.apple.mpegurl")) {
          // Safari / iOS native HLS.
          v.src = resolved;
          v.onerror = () => {
            scheduleReload();
          };
          v.play().catch(() => {});
        } else {
          state.value = "error";
        }
      } catch (_) {
        scheduleReload();
      }
    };

    const reload = () => {
      retryCount = 0;
      start();
    };

    watch(
      () => [props.streamKey, props.active, props.mode, props.deviceId],
      () => {
        retryCount = 0;
        if (props.active) start();
        else destroy();
      }
    );

    onMounted(() => {
      retryCount = 0;
      start();
    });
    onBeforeUnmount(destroy);

    return {
      videoEl,
      state,
      overlayText,
      loadLabel,
      loadProgress,
      loadStage,
      liveBadgeText,
      liveBadgeClass,
      reload,
    };
  },
});
</script>

<style scoped>
/* World-class "waking the camera" loader — broadcast pulse + eased, narrated
   progress. On-theme (live signal, brand green), not a generic spinner. */
.mw-loader {
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 0 24px;
  text-align: center;
  pointer-events: none;
  background: radial-gradient(
    120% 120% at 50% 42%,
    rgba(31, 157, 85, 0.16),
    rgba(6, 10, 8, 0.9) 64%
  );
  animation: mwLoaderIn 0.35s ease both;
}
@keyframes mwLoaderIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Broadcast pulse rings + breathing core */
.mw-loader__pulse {
  position: relative;
  width: 78px;
  height: 78px;
}
.mw-loader__ring {
  position: absolute;
  inset: 0;
  margin: auto;
  width: 78px;
  height: 78px;
  border-radius: 50%;
  border: 2px solid rgba(69, 201, 126, 0.5);
  transform: scale(0.55);
  opacity: 0;
  animation: mwPulse 2.4s cubic-bezier(0.22, 1, 0.36, 1) infinite;
}
.mw-loader__ring:nth-child(2) {
  animation-delay: 0.8s;
}
.mw-loader__ring:nth-child(3) {
  animation-delay: 1.6s;
}
@keyframes mwPulse {
  0% {
    transform: scale(0.55);
    opacity: 0.7;
  }
  70% {
    opacity: 0.15;
  }
  100% {
    transform: scale(1.95);
    opacity: 0;
  }
}
.mw-loader__core {
  position: absolute;
  inset: 0;
  margin: auto;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 22px;
  background: linear-gradient(145deg, #1f9d55, #157347);
  box-shadow: 0 8px 22px rgba(31, 157, 85, 0.45),
    inset 0 1px 2px rgba(255, 255, 255, 0.3);
  animation: mwBreathe 2.4s ease-in-out infinite;
}
@keyframes mwBreathe {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
}

/* Staged status label (re-mounts per stage via :key → replays fade) */
.mw-loader__label {
  color: #fff;
  font-size: 13.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
  min-height: 18px;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.55);
  animation: mwLabelIn 0.4s ease both;
}
@keyframes mwLabelIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Eased progress track with a moving shimmer so it feels alive between stages */
.mw-loader__track {
  width: min(220px, 72%);
  height: 4px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.16);
  overflow: hidden;
}
.mw-loader__fill {
  height: 100%;
  width: 0;
  border-radius: 99px;
  background: linear-gradient(90deg, #157347, #1f9d55, #45c97e, #1f9d55);
  background-size: 200% 100%;
  transition: width 1.1s cubic-bezier(0.22, 1, 0.36, 1);
  animation: mwShimmer 1.7s linear infinite;
}
@keyframes mwShimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Power-saving explainer microcopy */
.mw-loader__hint {
  display: flex;
  align-items: center;
  gap: 5px;
  color: rgba(255, 255, 255, 0.66);
  font-size: 10.5px;
  font-weight: 500;
}
.mw-loader__hint .bi {
  color: #45c97e;
}

@media (prefers-reduced-motion: reduce) {
  .mw-loader,
  .mw-loader__label {
    animation: none;
  }
  .mw-loader__core,
  .mw-loader__fill {
    animation: none;
  }
  .mw-loader__ring {
    animation-duration: 3.8s;
  }
}
</style>
